import type { CSSProperties } from "react";
import { useLayoutEffect, useState } from "react";

export type KeyboardViewportState = {
  keyboardInset: number;
  /** Visible viewport height in CSS px (shrinks when the on-screen keyboard is open). */
  visualViewportHeight: number;
  /** Layout viewport offset; non-zero when the browser pans the page (e.g. iOS with keyboard). */
  visualViewportOffsetTop: number;
};

/**
 * Bottom-sheet positioning for enquire drawers. When the keyboard is closed, only `maxHeight` is set so the
 * sheet can shrink to its content. When the keyboard is open (iOS), height + top anchor to the visual viewport.
 */
export function getEnquireSheetStyle(
  isOpen: boolean,
  viewport: KeyboardViewportState,
  panelMaxHeight: number | undefined,
): CSSProperties | undefined {
  if (!isOpen || !panelMaxHeight || viewport.visualViewportHeight <= 0) {
    return undefined;
  }
  if (viewport.keyboardInset > 0) {
    return {
      height: panelMaxHeight,
      maxHeight: panelMaxHeight,
      top:
        viewport.visualViewportOffsetTop +
        viewport.visualViewportHeight -
        panelMaxHeight,
      bottom: "auto",
    };
  }
  return { maxHeight: panelMaxHeight };
}

export const useKeyboardInset = (active = true): KeyboardViewportState => {
  const [state, setState] = useState<KeyboardViewportState>({
    keyboardInset: 0,
    visualViewportHeight: 0,
    visualViewportOffsetTop: 0,
  });

  useLayoutEffect(() => {
    if (!active || typeof window === "undefined") {
      setState({
        keyboardInset: 0,
        visualViewportHeight: 0,
        visualViewportOffsetTop: 0,
      });
      return;
    }

    const updateKeyboardInset = () => {
      const viewport = window.visualViewport;
      const layoutHeight = window.innerHeight;
      const visualHeight = viewport?.height ?? layoutHeight;
      const offsetTop = viewport?.offsetTop ?? 0;
      const inset = Math.max(
        0,
        Math.round(layoutHeight - (visualHeight + offsetTop)),
      );

      setState({
        keyboardInset: inset,
        visualViewportHeight: Math.round(visualHeight),
        visualViewportOffsetTop: Math.round(offsetTop),
      });
    };

    updateKeyboardInset();

    const viewport = window.visualViewport;
    viewport?.addEventListener("resize", updateKeyboardInset);
    viewport?.addEventListener("scroll", updateKeyboardInset);
    window.addEventListener("resize", updateKeyboardInset);

    return () => {
      viewport?.removeEventListener("resize", updateKeyboardInset);
      viewport?.removeEventListener("scroll", updateKeyboardInset);
      window.removeEventListener("resize", updateKeyboardInset);
    };
  }, [active]);

  return state;
};
